/**
 * "Ordo was updated — Refresh" toast.
 *
 * Delivery half of the service-worker update announcement (detection
 * lives in `sw/register.ts`, which stays import-free): mounted ONCE in
 * the app shell next to its `<Toaster />`. Listens for
 * `SW_UPDATED_EVENT` and also checks `isSwUpdateReady()` on mount — the
 * shell mounts after auth gates, so a takeover that fired during boot
 * would otherwise be missed (sonner does not replay toasts published
 * before its Toaster mounts).
 *
 * Suppression is structural first: /cafeteria and /kiosk render OUTSIDE
 * the app shell, so this hook never mounts there (kiosks have the admin
 * force-update flow instead, and order entry must never be interrupted).
 * The kioskContext check is belt-and-braces for a paired device that
 * somehow navigates into the shell.
 *
 * NEVER auto-reloads — the user chooses when via the toast action.
 */

import { toast } from '@ordo/ui/sonner'
import { useEffect } from 'react'

import { useProfileStore } from '@/stores/profile'
import { isSwUpdateReady, SW_UPDATED_EVENT } from '@/sw/register'

export function useSwUpdateToast(): void {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const announce = () => {
      if (useProfileStore.getState().profile?.kioskContext.paired === true) {
        return
      }
      // Stable id: repeat announcements (multiple deploys in one session)
      // update the existing toast in place instead of stacking.
      toast('Ordo was updated', {
        id: 'sw-updated',
        duration: Number.POSITIVE_INFINITY,
        description: 'Refresh to load the latest version.',
        action: { label: 'Refresh', onClick: () => window.location.reload() },
      })
    }
    if (isSwUpdateReady()) {
      announce()
    }
    window.addEventListener(SW_UPDATED_EVENT, announce)
    return () => window.removeEventListener(SW_UPDATED_EVENT, announce)
  }, [])
}
